import { useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';
import { closeHistory } from '@tiptap/pm/history';
import { Dialog } from '../dialogs/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { defaultEmbed, ExternalEmbedAttrs, parseEmbedSnippet, validateEmbedUrl, validateEmbedWidth } from './external-embed';

export function ExternalEmbedDialog({ editor, request }: { editor: Editor; request: number }) {
    const [draft, setDraft] = useState<ExternalEmbedAttrs | null>(null);
    const [source, setSource] = useState<'url' | 'snippet'>('url');
    const [snippet, setSnippet] = useState('');
    const [error, setError] = useState('');
    const context = useRef<{ doc: typeof editor.state.doc; from: number; to: number; existing: boolean } | null>(null);
    const handled = useRef(0);

    const open = useCallback((position?: number) => {
        const selection = editor.state.selection;
        const from = position ?? selection.from;
        const node = editor.state.doc.nodeAt(from);
        const existing = node?.type.name === 'externalEmbed';
        context.current = { doc: editor.state.doc, from, to: existing ? from + node!.nodeSize : selection.to, existing };
        setDraft(existing ? { ...node!.attrs } as ExternalEmbedAttrs : { ...defaultEmbed });
        setSource('url'); setSnippet(''); setError('');
    }, [editor]);

    useEffect(() => {
        setDraft(null); handled.current = 0;
        const storage = editor.storage.externalEmbed;
        storage.onEdit = (position: number) => open(position);
        return () => { storage.onEdit = null; };
    }, [editor, open]);
    useEffect(() => {
        if (request && request !== handled.current && !editor.isDestroyed) { handled.current = request; open(); }
    }, [request, editor, open]);

    function importSnippet() {
        try { setDraft(parseEmbedSnippet(snippet)); setSource('url'); setError(''); }
        catch (failure) { setError((failure as Error).message); }
    }
    function save() {
        if (!draft || !context.current) return;
        try {
            if (source === 'snippet') throw new Error('Read the iframe snippet before saving.');
            const attrs = { ...draft, src: validateEmbedUrl(draft.src), width: validateEmbedWidth(draft.width) };
            if (!Number.isInteger(attrs.height) || attrs.height < 20 || attrs.height > 10000) throw new Error('Height must be between 20 and 10000 pixels.');
            if (!attrs.title.trim()) throw new Error('Enter an accessibility title for the embedded content.');
            if (editor.isDestroyed || !editor.state.doc.eq(context.current.doc)) throw new Error('The content has changed. Close this dialog and open it again.');
            editor.chain().focus().command(({ tr }) => { closeHistory(tr); return true; })
                .insertContentAt({ from: context.current.from, to: context.current.to }, { type: 'externalEmbed', attrs }).run();
            setDraft(null);
        } catch (failure) { setError((failure as Error).message); }
    }
    const change = (values: Partial<ExternalEmbedAttrs>) => setDraft(current => current ? { ...current, ...values } : current);
    return <Dialog header={context.current?.existing ? 'Edit external content' : 'Insert external content'} visible={!!draft}
        onHide={() => setDraft(null)} modal style={{ width: '38rem', maxWidth: '95vw' }}
        footer={<><Button label="Cancel" outlined onClick={() => setDraft(null)} /><Button label={context.current?.existing ? 'Save' : 'Insert'} onClick={save} /></>}>
        {draft && <div className="flex flex-column gap-3">
            {error && <div role="alert" className="p-3 border-round" style={{ background: 'var(--red-100)', color: 'var(--red-900)', border: '1px solid var(--red-500)' }}><i className="pi pi-exclamation-triangle mr-2" />{error}</div>}
            <label htmlFor="embed-source">Source</label>
            <select id="embed-source" className="p-inputtext" value={source} onChange={event => { setSource(event.target.value as 'url' | 'snippet'); setError(''); }}>
                <option value="url">Embed URL</option><option value="snippet">Iframe snippet</option>
            </select>
            {source === 'snippet' ? <><label htmlFor="embed-snippet">Iframe snippet</label>
                <InputTextarea id="embed-snippet" value={snippet} rows={4} onChange={event => setSnippet(event.target.value)} />
                <Button label="Read snippet" outlined onClick={importSnippet} />
            </> : <><label htmlFor="embed-url">Embed URL</label><InputText id="embed-url" value={draft.src} onChange={event => change({ src: event.target.value })} /></>}
            <label htmlFor="embed-title">Accessibility title</label><InputText id="embed-title" value={draft.title} onChange={event => change({ title: event.target.value })} />
            <div className="flex gap-3 flex-wrap">
                <div className="flex flex-column gap-2 flex-1"><label htmlFor="embed-width">Width (pixels or %)</label><InputText id="embed-width" value={draft.width} onChange={event => change({ width: event.target.value })} /></div>
                <div className="flex flex-column gap-2 flex-1"><label htmlFor="embed-height">Height (pixels)</label><InputText id="embed-height" type="number" min={20} max={10000} value={String(draft.height || '')} onChange={event => change({ height: Number(event.target.value) })} /></div>
            </div>
            <label htmlFor="embed-align">Alignment</label><select id="embed-align" className="p-inputtext" value={draft.align} onChange={event => change({ align: event.target.value as ExternalEmbedAttrs['align'] })}>
                <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
            </select>
            <label className="flex align-items-center gap-2"><input type="checkbox" checked={draft.fullscreen} onChange={event => change({ fullscreen: event.target.checked })} />Allow fullscreen</label>
        </div>}
    </Dialog>;
}
